/**
 * Options and hints of the «Зафиксировать» fields (design QuickCapture.dc.html `combo` items,
 * the pickers): people with role and stage, projects with client, clients with country.
 */
import { moneyRu } from '@outsource/shared';
import type { CaptureLookups, LookupAssignment, LookupInvoice, LookupPerson, LookupProject } from '@outsource/shared';
import type { ComboItem } from './fieldSpec';

export const STAGE: Readonly<Record<LookupPerson['status'], string>> = {
  candidate: 'кандидат',
  onboarding: 'онбординг',
  active: 'работает',
  offboarding: 'оффбординг',
  left: 'ушёл',
};
export const RATE_FORMATS = [
  ['hourly', 'В час'],
  ['monthly', 'Фикс в месяц'],
  ['scope', 'Под ключ'],
] as const;
/** A person's pay on a project may also be a share or the salary through an entity (V169). */
export const ASSIGN_RATE_FORMATS = [
  ...RATE_FORMATS.map(([value, label]) => ({ value, label })),
  /** V184: the person's rate × the hours billed to the client. */
  { value: 'billed_hourly', label: 'За выставленный час' },
  { value: 'revenue_share', label: '% от выручки' },
  { value: 'profit_share', label: '% от прибыли' },
  { value: 'salary', label: 'Зарплата через юрлицо' },
] as const;

export const isShareFormat = (format: string): boolean => format === 'revenue_share' || format === 'profit_share';

export const UNIT: Readonly<Record<string, string>> = {
  hourly: 'в час',
  monthly: 'в месяц',
  scope: 'за проект',
  billed_hourly: 'за выставленный час',
};
export const RATE_LABEL: Readonly<Record<string, string>> = {
  hourly: 'Ставка на этом проекте',
  billed_hourly: 'Ставка за выставленный час',
  monthly: 'Фикс в месяц на этом проекте',
  scope: 'Сумма под ключ',
};

export const personItems = (
  l: CaptureLookups,
  keep: (p: LookupPerson) => boolean = () => true,
): ComboItem[] =>
  l.people
    .filter(keep)
    .map((p) => ({ id: p.id, label: p.name, sub: `${p.role ?? '—'} · ${STAGE[p.status]}` }));
export const projectItems = (
  l: CaptureLookups,
  keep: (p: LookupProject) => boolean = () => true,
): ComboItem[] =>
  l.projects.filter(keep).map((p) => ({ id: p.id, label: p.name, sub: p.clientName ?? 'без клиента' }));
export const clientItems = (l: CaptureLookups): ComboItem[] =>
  l.clients.map((c) => ({ id: c.id, label: c.name, sub: c.country ?? '—' }));
export const anyItems = (l: CaptureLookups): ComboItem[] => [
  ...l.people.map((p) => ({ id: `person:${p.id}`, label: p.name, sub: 'человек' })),
  ...l.projects.map((p) => ({ id: `project:${p.id}`, label: p.name, sub: p.clientName ?? 'проект' })),
  ...l.clients.map((c) => ({ id: `client:${c.id}`, label: c.name, sub: 'клиент' })),
];

/** The client's documents open for a payment (V187), oldest first. */
export const invoicesOf = (l: CaptureLookups, clientId: string | null): LookupInvoice[] =>
  clientId ? l.invoices.filter((i) => i.clientId === clientId) : [];
/** «1054-2026 · авг 2026 — открыто 3 120,00 USD» after the empty choice. */
export function invoiceOptions(l: CaptureLookups, clientId: string | null): Array<{ value: string; label: string }> {
  const open = invoicesOf(l, clientId);
  const empty = !clientId ? 'Сначала выберите клиента' : open.length ? 'Выберите счёт' : 'Нет открытых счетов';
  return [
    { value: '', label: empty },
    ...open.map((i) => ({ value: i.id, label: `${i.label} — открыто ${moneyRu(i.openMinor, i.currency)}` })),
  ];
}

/** The person's running assignments; `withEnded` adds ended ones with unpaid months (V182). */
export const assignmentsOf = (l: CaptureLookups, personId: string | null, withEnded = false): LookupAssignment[] =>
  personId ? l.assignments.filter((a) => a.personId === personId && (withEnded || !a.endedOn)) : [];


/** The entities a leave can cover (V235): those whose salary pays for one of the projects picked. */
export const leaveEntityIds = (l: CaptureLookups, assignmentIds: readonly string[]): string[] => [
  ...new Set(l.assignments.filter((a) => assignmentIds.includes(a.id)).flatMap((a) => a.salaryEntityIds)),
];
